import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react'
import { useCurrentVariant } from 'hooks/useCurrentVariant'
import { AudioTags } from 'components/AudioTags'

interface AudioState {
  isPlaying: boolean
  playAudioProgrammatically: () => void
  stopAudioProgrammatically: () => void
  togglePlay: () => void
}

const AudioContext = createContext<AudioState | null>(null)

export const usePersistedMute = () => {
  const setIsPersistedMute = useCallback((isMuted: boolean) => {
    localStorage.setItem('isMuted', JSON.stringify(isMuted))
  }, [])

  const isSsr = typeof window === 'undefined'

  const isPersistedMute = !isSsr
    ? JSON.parse(localStorage.getItem('isMuted') ?? 'false')
    : false

  return { isPersistedMute, setIsPersistedMute }
}

interface AudioProviderProps {
  children: ReactNode
}

export const AudioProvider = ({ children }: AudioProviderProps) => {
  const { isPersistedMute, setIsPersistedMute } = usePersistedMute()
  const [isPlaying, setIsPlaying] = useState(false)

  const {
    currentVariant: { slug },
  } = useCurrentVariant()

  const context = useMemo<AudioState>(
    () => ({
      isPlaying,
      playAudioProgrammatically() {
        if (!isPersistedMute) {
          setIsPlaying(true)
        }
      },
      stopAudioProgrammatically() {
        setIsPlaying(false)
      },
      togglePlay() {
        setIsPlaying((wasPlaying) => {
          setIsPersistedMute(wasPlaying)
          return !wasPlaying
        })
      },
    }),
    [isPlaying, isPersistedMute, setIsPersistedMute]
  )

  const handlePlayError = useCallback(() => {
    setIsPlaying(false)
  }, [])

  return (
    <AudioContext.Provider value={context}>
      <AudioTags
        isPlaying={isPlaying}
        currentVariantSlug={slug}
        onPlayError={handlePlayError}
      />

      {children}
    </AudioContext.Provider>
  )
}

export const useAudioContext = () => {
  const context = useContext(AudioContext)
  if (!context) {
    throw new Error('useAudioContext must be used within an AudioProvider')
  }
  return context
}
